import {
  Braces,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileArchive,
  Loader2,
  TriangleAlert,
  X,
} from 'lucide-react';
import type { PendingAttachment } from './types';

interface AttachmentChipProps {
  attachment: PendingAttachment;
  onRemove: (localId: string) => void;
  onOpen?: (attachmentId: string) => void;
}

function iconFor(file: File) {
  const type = file.type || '';
  const name = file.name.toLowerCase();
  if (type.startsWith('image/')) return FileImage;
  if (name.endsWith('.csv') || name.endsWith('.tsv') || type.includes('spreadsheet')) {
    return FileSpreadsheet;
  }
  if (name.endsWith('.json') || name.endsWith('.xml') || name.endsWith('.yaml')) return Braces;
  if (name.endsWith('.zip')) return FileArchive;
  return FileText;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function statusLabel(attachment: PendingAttachment): string | null {
  if (attachment.status === 'uploading') return 'Uploading…';
  if (attachment.status === 'error') return attachment.error ?? 'Failed';
  if (attachment.serverStatus === 'ocr_pending') return 'OCR pending';
  if (attachment.serverStatus === 'zip_ready') return 'Archive ready';
  if (attachment.ocrProvider) return `OCR · ${attachment.ocrProvider}`;
  if ((attachment.extractedChars ?? 0) > 0) return `${attachment.extractedChars} chars`;
  return null;
}

function AttachmentChip({ attachment, onRemove, onOpen }: AttachmentChipProps) {
  const { file, status } = attachment;
  const Icon = iconFor(file);
  const label = statusLabel(attachment);
  const clickable = status === 'ready' && attachment.id && onOpen;
  const refHint = attachment.shortRef
    ? `ref ${attachment.shortRef}`
    : attachment.referenceId
      ? `ref ${attachment.referenceId.slice(-8)}`
      : null;
  const serverHint = attachment.onServer
    ? attachment.ocisPath
      ? `on server · ${attachment.ocisPath}`
      : 'on server'
    : null;
  const titleParts = [attachment.summary || file.name, refHint, serverHint].filter(Boolean);

  return (
    <div
      className={`group flex items-center gap-2 rounded-lg border px-2 py-1.5 text-xs transition ${
        status === 'error'
          ? 'border-zt-danger/40 bg-zt-danger/5'
          : 'border-zt-border bg-zt-bg hover:border-zt-primary/40'
      }`}
      title={titleParts.join('\n')}
    >
      {attachment.previewUrl ? (
        <img
          src={attachment.previewUrl}
          alt=""
          className="h-8 w-8 shrink-0 rounded object-cover"
        />
      ) : (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-zt-elevated">
          {status === 'uploading' ? (
            <Loader2 size={14} className="animate-spin text-zt-info" />
          ) : status === 'error' ? (
            <TriangleAlert size={14} className="text-zt-danger" />
          ) : (
            <Icon size={14} className="text-zt-primary" />
          )}
        </span>
      )}

      <button
        type="button"
        disabled={!clickable}
        onClick={() => clickable && onOpen?.(attachment.id!)}
        className="min-w-0 text-left disabled:cursor-default"
      >
        <span className="block max-w-[11rem] truncate text-slate-200 group-hover:text-white">
          {file.name}
        </span>
        <span className="block truncate text-[10px] text-zt-muted">
          {formatBytes(file.size)}
          {label ? ` · ${label}` : ''}
          {refHint ? ` · ${refHint}` : ''}
          {attachment.onServer ? ' · on server' : ''}
        </span>
      </button>

      {attachment.learned?.entityName && (
        <span
          className="max-w-[10rem] truncate rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-400"
          title="Entity facts saved to memory"
        >
          Learned: {attachment.learned.entityName}
        </span>
      )}

      <button
        type="button"
        onClick={() => onRemove(attachment.localId)}
        className="shrink-0 text-zt-muted transition hover:text-slate-200"
        aria-label={`Remove ${file.name}`}
      >
        <X size={13} />
      </button>
    </div>
  );
}

export default AttachmentChip;
